import React from 'react';
import { saveAs } from 'file-saver';
import { Button } from '../../tailwindcomponents';
import CertificatesApi from '../../api/certificates';
import {Certificates, CertificateRights} from '../../types/certificates';
import { PdfDocument } from '../../utils/pdf/PdfDocument';

type PDFCreateProps = {
    buttonText?: string;
    setLoading: Function;
    loading: boolean;
    selectedType: string;
    orderByText: string;
    ascending: boolean;
    selectedAircraftOptions: number[];
    selectedRatingOptions: number[];
    onlyValid:  boolean;
    selectedEmployeeId: number;
    selectedDateFrom: Date | null;
    selectedDateTo: Date | null;
    children?: React.ReactNode;
    isOnlySelectedFields: boolean;
};

const PDFCreate: React.FC<PDFCreateProps> = ({
    buttonText = 'Download PDF',
    setLoading,
    loading,
    selectedType,
    orderByText,
    ascending,
    selectedAircraftOptions,
    selectedRatingOptions,
    onlyValid,
    selectedEmployeeId,
    selectedDateFrom,
    selectedDateTo,
    isOnlySelectedFields
}) => {
    const getExportData = async () => {
        try {
            setLoading(true);

            const res = await CertificatesApi.GetCertificateHistoricalDataToPdf(
                selectedType,
                orderByText,
                ascending,
                selectedAircraftOptions,
                selectedRatingOptions,
                onlyValid,
                selectedEmployeeId,
                selectedDateFrom,
                selectedDateTo,
                isOnlySelectedFields
            );
            const blob = buildCertificatesPdf(res.data);
            saveAs(blob, 'Certificates_Export.pdf');
        } catch (err) {
            console.error('PDF generation failed:', err);
        } finally {
            setLoading(false);
        }
    };

    const mainRowColumnNames: (keyof Certificates)[] = [
        'mechanic_full_name'
    ];

    const subRowColumnNames: (keyof CertificateRights)[] = [
        'rating_name',
        'aircraft_category_name',
        'expiration_date_text'
    ];

    const mainRowColumnHeaders: string[] = [
        'Rating',
        'Aircraft',
        'Expiration date'
    ];

    const buildCertificatesPdf = (rows: Certificates[]): Blob => {
        const doc = new PdfDocument({ margins: { top: 10, right: 10, bottom: 0, left: 10 } });
        const colWidth = (count: number) => doc.width / count;

        doc.text('Icelandair – List of Certifying staff', { size: 18, marginBottom: 8 });

        // ── table header ───────────────────────
        doc.row(
            mainRowColumnHeaders.map(h => ({ width: colWidth(mainRowColumnHeaders.length), text: h, style: { font: 'Helvetica-Bold', size: 12 } })),
            { paddingY: 4, borderBottom: { color: '#000000', width: 2 } },
        );

        // ── data rows: one block per employee, never split across pages ──
        rows.forEach(emp => doc.keepTogether(() => {
            // main row
            doc.row(
                mainRowColumnNames.map(col => ({ width: colWidth(mainRowColumnNames.length), text: `${emp[col]} (${emp.mechanic_employee_id})`, style: { size: 10 } })),
                { paddingY: 4 },
            );
            // sub-rows
            emp.rightsForCertificate?.forEach(sub => doc.row(
                subRowColumnNames.map(col => ({ width: colWidth(subRowColumnNames.length), text: String(sub[col] ?? ''), style: { size: 8 } })),
                { paddingY: 2, background: '#DCDCDC' },
            ));
            doc.drawRect(doc.x, doc.y, doc.width, 1, '#000000');
            doc.moveDown(1);
        }));

        return doc.toBlob();
    };

    const handleDownload = async () => {
        setLoading(true);
        try {
            await Promise.resolve(getExportData()); 
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('PDF generation failed:', err);
        }
    };

    return (
        <Button variant="contained" onClick={handleDownload}>
            {loading ? 'Please wait…' : buttonText}
        </Button>
    );
};

export default PDFCreate;
